'use strict';

import dayjs from 'dayjs';
import { getTierSampleBounds } from './studyBuilder.config.js';

const SIZE_TIER_BASE = {
	'1-30': 8200,
	'31-120': 11200,
	'121-500': 15500,
	'501-1000': 22800,
	'1000+': 31500,
};

const PRODUCT_ADJUST = {
	'moa-discovery': 1.08,
	'global-discovery': 1,
	'microbiome-smartpanel': 1.06,
};

const DEFAULT_WEEKS_TO_COMPLETION = 7;

/**
 * @param {object} params
 * @param {string} params.productId
 * @param {string} params.studySizeId
 * @param {number} params.numberOfSamples
 * @param {string} params.sampleKit - 'yes' | 'no'
 * @param {string} [params.plannedShippingDate] - ISO or parseable date string
 * @param {string} [params.statsServices] - 'yes' | 'no'
 * @param {string} [params.interpretationLevel] - 'none' | 'standard' | 'advanced'
 * @returns {{ estimatedCost: number, estimatedCompletionDate: import('dayjs').Dayjs }}
 */
export function computeMockQuote({
	productId,
	studySizeId,
	numberOfSamples,
	sampleKit,
	plannedShippingDate,
	statsServices,
	interpretationLevel,
}) {
	const base = SIZE_TIER_BASE[studySizeId] ?? SIZE_TIER_BASE['121-500'];
	const prodMult = PRODUCT_ADJUST[productId] ?? 1;
	const n = Number(numberOfSamples) || 0;
	const sampleFactor = 1 + Math.min(n / 800, 0.35);
	let serviceMult = 1;
	if (statsServices === 'yes') serviceMult *= 1.06;
	if (interpretationLevel === 'standard') serviceMult *= 1.04;
	if (interpretationLevel === 'advanced') serviceMult *= 1.12;
	const kitFee = sampleKit === 'yes' ? 850 : 0;
	const estimatedCost = Math.round(base * prodMult * sampleFactor * serviceMult + kitFee);

	const start = plannedShippingDate ? dayjs(plannedShippingDate) : dayjs();
	const estimatedCompletionDate = start.add(DEFAULT_WEEKS_TO_COMPLETION, 'week');

	return { estimatedCost, estimatedCompletionDate };
}

/**
 * Indicative cost range using the lower and upper sample counts for the selected study size tier.
 * @param {{ productId: string, studySizeId: string, sampleKit: string, plannedShippingDate?: string, statsServices?: string, interpretationLevel?: string }} params
 * @returns {{ estimatedCostLow: number, estimatedCostHigh: number, estimatedCompletionDate: import('dayjs').Dayjs }}
 */
export function computeMockQuoteTierRange(params) {
	const { lo, hi } = getTierSampleBounds(params.studySizeId);
	const lowQ = computeMockQuote({ ...params, numberOfSamples: lo });
	const highQ = computeMockQuote({ ...params, numberOfSamples: hi });
	return {
		estimatedCostLow: lowQ.estimatedCost,
		estimatedCostHigh: highQ.estimatedCost,
		estimatedCompletionDate: lowQ.estimatedCompletionDate,
	};
}

export function formatCurrencyUSD(amount) {
	return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
}

/**
 * @param {number} low
 * @param {number} high
 * @returns {string}
 */
export function formatCurrencyRangeUSD(low, high) {
	if (!Number.isFinite(low) || !Number.isFinite(high)) return '—';
	if (low === high) return formatCurrencyUSD(low);
	return `${formatCurrencyUSD(low)} – ${formatCurrencyUSD(high)}`;
}
